import React, { useCallback, useEffect, useMemo, useState } from 'react';
// MUI
import {
  Card,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { ThumbUp, Edit, Delete, Add } from '@mui/icons-material';
import { GridColDef, GridFilterModel, GridOverlay } from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';

import moment from 'moment';

import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../../utils/adminDashboardUtils';
import { fileNameFromUrl } from '../../../utils/fileUtils';
import BaseToolbar from '../../Datagrid/BaseToolbar';
import GridPaginationBar from '../GridPaginationBar';
import PartnerResourcesModal from '../../PartnerResources/PartnerResourcesModal';
import fetchPartnerResources, {
  deletePartnerResource,
  fetchResourceCategories,
  patchPartnerResource,
  patchPartnerResourceImages,
  postPartnerResource,
  RESOURCE_CATEGORY_TITLES,
} from '../../../services/partnerResources';

import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';
import {
  PartnerResource,
  PartnerResourceCategory,
  PartnerResourceImage,
  ResourceImageType, 
} from '../../../interfaces/partnerResources.interface';
import { getResourceImageUrl } from '../../../services/adminUtils';


const PoliciesAdmin = () => {
  const [rows, setRows] = useState<PartnerResource[]>([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [loading, setLoading] = useState(true);
  const [showPRCPreview, setShowPRCPreview] = useState(false);
  const [policiesCategoryId, setPoliciesCategoryId] = useState<number | null>(null);

  // Add modal state
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSubDescription, setNewSubDescription] = useState('');
  const [addError, setAddError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newDocFile, setNewDocFile] = useState<File | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [newImagePreviewUrl, setNewImagePreviewUrl] = useState<string | null>(null);
  const [newDocPreviewUrl, setNewDocPreviewUrl] = useState<string | null>(null);

  // Edit modal state
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PartnerResource | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSubDescription, setEditSubDescription] = useState('');
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editDocFile, setEditDocFile] = useState<File | null>(null);
  const [editDocPreviewUrl, setEditDocPreviewUrl] = useState<string | null>(null);
  const [editImagePreviewUrl, setEditImagePreviewUrl] = useState<string | null>(null);

  // Delete modal state
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState<PartnerResource | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  function CustomToolbar(props: { openInviteAdminModal: () => void }) {
    const { openInviteAdminModal, ...rest } = props;

    return (
      <BaseToolbar
        {...rest}
        csvOptions={{ fileName: `policies_${moment().format('MM_DD_YYYY')}` }}
      >
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={() => setNewModalOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Policy
          </Button>
        </Box>
      </BaseToolbar>
    );
  }

  const getResourceDocUrl = (r?: PartnerResource | null) =>
    r?.images?.find((img) => img.type === ResourceImageType.DOCUMENT)?.image || '';

  const getResourceDocName = (r?: PartnerResource | null) => fileNameFromUrl(getResourceDocUrl(r));

  const fetchPolicies = useCallback(
    async (categoryId: number) => {
      setLoading(true);
      try {
        const { count, results } = await fetchPartnerResources({
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
          ordering:
            sortModel.length > 0
              ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}`
              : undefined,
          filterModel: filterModelApplied,
          categoryId,
        });

        setRows(results);
        setRowCount(count);
      } catch (error) {
        console.error('Error fetching policies:', error);
      } finally {
        setLoading(false);
      }
    },
    [paginationModel, sortModel, filterModelApplied],
  );

  const handleFetchResourceCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { results } = await fetchResourceCategories();

      const policiesId = results.find(
        (cat: PartnerResourceCategory) => cat.title === RESOURCE_CATEGORY_TITLES.POLICIES,
      )?.id;

      if (policiesId) {
        setPoliciesCategoryId(policiesId);
        await fetchPolicies(policiesId);
      } else {
        setPoliciesCategoryId(null);
        console.warn('POLICIES category not found. Cannot fetch policies.');
      }
    } catch (error) {
      console.error('Error fetching resource categories:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchPolicies]);

  useEffect(() => {
    handleFetchResourceCategories();
  }, [handleFetchResourceCategories]);

  useEffect(() => {
    if (policiesCategoryId) {
      fetchPolicies(policiesCategoryId);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filterModelApplied,
    policiesCategoryId,
    fetchPolicies,
  ]);

  const handleOpenEdit = useCallback((row: PartnerResource) => {
    setEditingRow(row);
    setEditTitle(row.title ?? '');
    setEditDescription(row.description ?? '');
    setEditSubDescription(row.subDescription ?? '');
    setEditImageFile(null);
    setEditDocFile(null);
    setEditError('');
    setIsEditOpen(true);
  }, []);

  const handleCloseEdit = useCallback(() => {
    if (isSaving) return;

    setIsEditOpen(false);
    setEditingRow(null);
    setEditTitle('');
    setEditUrl('');
    setEditDescription('');
    setEditError('');
  }, [isSaving]);

  const handleOpenDelete = useCallback((row: PartnerResource) => {
    setDeletingRow(row);
    setIsDeleteOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    if (isDeleting) return;

    setIsDeleteOpen(false);
    setDeletingRow(null);
  }, [isDeleting]);

  const handleCloseAdd = useCallback(() => {
    if (isAdding) return;

    setNewTitle('');
    setNewDescription('');
    setNewSubDescription('');
    setAddError('');
    setNewModalOpen(false);
  }, [isAdding]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingRow) return;

    if (!editTitle.trim()) {
      setEditError('Title is required.');
      return;
    }

    if (!editDescription.trim()) {
      setEditError('Description is required.');
      return;
    }

    if (!editSubDescription.trim()) {
      setEditError('Sub Description is required.');
      return;
    }

    if (!editImageFile && !getResourceImageUrl(editingRow)) {
      setEditError('Image is required. Please upload an image or ensure the existing image is valid.');
      return;
    }

    if (!editDocFile && !getResourceDocUrl(editingRow)) {
      setEditError('Document is required. Please upload a document or ensure the existing document is valid.');
      return;
    }

    setIsSaving(true);
    try {
      setEditError('');

      await patchPartnerResourceImages(
        Number(editingRow.id),
        editImageFile,
        editDocFile,
      );

      const updatedResource = await patchPartnerResource(Number(editingRow.id), {
        title: editTitle,
        description: editDescription,
        subDescription: editSubDescription,
      });

      setRows((prev) =>
        prev.map((r) =>
          r.id === editingRow.id
            ? {
              ...r,
              ...updatedResource,
            }
            : r,
        ),
      );

      handleCloseEdit();
      setShowSaved(true);
    } catch (error) {
      console.error('Error updating policy:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editingRow, editTitle, editUrl, editDescription, editSubDescription, handleCloseEdit, editImageFile, editDocFile]);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deletingRow) return;

    setIsDeleting(true);
    try {
      const success = await deletePartnerResource(Number(deletingRow.id));
      if (!success) {
        console.error('Failed to delete policy. Please try again.');
        return;
      }

      setRows((prev) => prev.filter((r) => r.id !== deletingRow.id));
      setRowCount((prev) => Math.max(0, prev - 1));
      handleCloseDelete();
      setShowSaved(true);
    } catch (error) {
      console.error('Error deleting policy:', error);
    } finally {
      setIsDeleting(false);
    }
  }, [deletingRow, handleCloseDelete]);

  const handleAddNewPolicy = useCallback(async () => {
    if (!newTitle.trim()) {
      setAddError('Title is required.');
      return;
    }

    if (!newDescription.trim()) {
      setAddError('Description is required.');
      return;
    }

    if (!newSubDescription.trim()) {
      setAddError('Sub Description is required.');
      return;
    }

    if (!newImageFile) {
      setAddError('Image is required.');
      return;
    }

    if (!newDocFile) {
      setAddError('Document is required.');
      return;
    }

    setAddError('');
    setIsAdding(true);

    try {
      if (!policiesCategoryId) {
        console.error('No category found for POLICIES. Cannot add new policy.');
        return;
      }

      if (!rows[0]?.author) {
        console.error('No author info available from existing policies. Cannot add new policy.');
        return;
      }

      const created = await postPartnerResource({
        category: policiesCategoryId,
        author: rows[0].author.id,
        title: newTitle,
        description: newDescription,
        sub_description: newSubDescription,
      });

      const createdImages = await patchPartnerResourceImages(
        Number(created.id),
        newImageFile,
        newDocFile,
      );

      created.images = createdImages.images;

      setRows((prev) => [created, ...prev]);
      setRowCount((prev) => prev + 1);

      handleCloseAdd();
      setShowSaved(true);
      setNewImageFile(null);
      setNewDocFile(null);
    } catch (error) {
      console.error('Error adding new policy:', error);
    } finally {
      setIsAdding(false);
    }
  }, [newTitle, newDescription, newSubDescription, policiesCategoryId, rows, handleCloseAdd, newImageFile, newDocFile]);

  const columns: GridColDef<PartnerResource>[] = useMemo(
    () => [
      {
        field: 'imageUrl',
        headerName: 'Image',
        width: 100,
        filterable: false,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        disableColumnMenu: true,
        renderCell: (params) =>
          params.row.images.find((img) => img.type === 'resource_image') ? (
            <img
              src={params.row.images.find((img) => img.type === 'resource_image')?.image}
              alt={params.row.title}
              style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
            />
          ) : (
            <Box
                sx={{
                  width: 60,
                  height: 60,
                  backgroundColor: 'grey.300',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
            >
                <Typography variant="caption" color="textSecondary">
                    No Image
                </Typography>
            </Box>
          ),
      },
      {
        field: 'title',
        headerName: 'Policy',
        filterOperators: stringContainsOnly,
        width: 260,
        valueGetter: (params) => params.row.title,
        valueFormatter: (params) => params.value || 'Unknown',
        renderCell: (params) => (
            <a href={params.row.images.find((img) => img.type === 'resource_document')?.image || '#'} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                    {params.value || 'Unknown'}
                </Typography>
            </a>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        filterOperators: stringContainsOnly,
        width: 420,
        valueGetter: (params) => params.row.description,
        valueFormatter: (params) => params.value || 'Unknown',
      },
      {
        field: 'sub_description',
        headerName: 'Sub Description',
        filterOperators: stringContainsOnly,
        width: 520,
        valueGetter: (params) => params.row.subDescription,
        valueFormatter: (params) => params.value || '',
      },
      {
        field: 'updated_at',
        headerName: 'Updated At',
        filterOperators: stringContainsOnly,
        width: 180,
        valueGetter: (params) =>
          params.row.updatedAt ? moment(params.row.updatedAt).format('M/D/YYYY') : 'Unknown',
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 105,
        align: 'right',
        headerAlign: 'right',
        disableColumnMenu: true,
        cellClassName: 'actionsCell',
        renderCell: (params) => (
            <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
            }}
            >
            <Tooltip title="Edit">
                <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEdit(params.row);
                }}
                >
                <Edit fontSize="small" />
                </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
                <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDelete(params.row);
                }}
                >
                <Delete fontSize="small" />
                </IconButton>
            </Tooltip>
            </Box>
        ),
      },
    ],
    [handleOpenDelete, handleOpenEdit],
  );

  useEffect(() => {
    if (!editImageFile) {
      setEditImagePreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(editImageFile);
    setEditImagePreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [editImageFile]);

  useEffect(() => {
    if (!newImageFile) return setNewImagePreviewUrl(null);
    const url = URL.createObjectURL(newImageFile);
    setNewImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newImageFile]);

  useEffect(() => {
    if (!newDocFile) return setNewDocPreviewUrl(null);
    const url = URL.createObjectURL(newDocFile);
    setNewDocPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [newDocFile]);


  useEffect(() => {
    if (!editDocFile) {
      setEditDocPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(editDocFile);
    setEditDocPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [editDocFile]);


  const previewRows = useMemo(() => {
 
    if (editingRow) {
      const existingImg = (editingRow.images ?? []).find((img) => img.type === ResourceImageType.IMAGE);
      const existingDoc = (editingRow.images ?? []).find((img) => img.type === ResourceImageType.DOCUMENT);

      const nowIso = new Date().toISOString();
      const resourceId = Number(editingRow.id);

      const patchedImages: PartnerResourceImage[] = [
        ...(editingRow.images ?? []).filter(
          (img) => img.type !== ResourceImageType.IMAGE && img.type !== ResourceImageType.DOCUMENT,
        ),
        {
          ...(existingImg ?? ({} as PartnerResourceImage)),
          type: ResourceImageType.IMAGE,
          image: editImagePreviewUrl ?? existingImg?.image ?? '',
          createdAt: existingImg?.createdAt ?? nowIso,
          updatedAt: nowIso,
          resource: existingImg?.resource ?? resourceId,
          id: existingImg?.id ?? -1,
        },
        {
          ...(existingDoc ?? ({} as PartnerResourceImage)),
          type: ResourceImageType.DOCUMENT,
          image: editDocPreviewUrl ?? existingDoc?.image ?? '',
          createdAt: existingDoc?.createdAt ?? nowIso,
          updatedAt: nowIso,
          resource: existingDoc?.resource ?? resourceId,
          id: existingDoc?.id ?? -1,
        },
      ].filter((img) => Boolean(img.image));

      const editedRow: PartnerResource = {
        ...editingRow,
        title: editTitle,
        description: editDescription,
        subDescription: editSubDescription,
        images: patchedImages,
      };

      const rest = rows.filter((r) => r.id !== editingRow.id);
      return [editedRow, ...rest];
    }

    const shouldShowNewDraft = newModalOpen && showPRCPreview;
    if (shouldShowNewDraft) {
      const nowIso = new Date().toISOString();
      const draftImages: PartnerResourceImage[] = [
        ...(newImagePreviewUrl
          ? [
            {
              id: -1,
              type: ResourceImageType.IMAGE,
              image: newImagePreviewUrl,
              createdAt: nowIso,
              updatedAt: nowIso,
              resource: -1,
            },
          ]
          : []),
        ...(newDocPreviewUrl
          ? [
            {
              id: -2,
              type: ResourceImageType.DOCUMENT,
              image: newDocPreviewUrl,
              createdAt: nowIso,
              updatedAt: nowIso,
              resource: -1,
            },
          ]
          : []),
      ];

      const draft: PartnerResource = {
        ...rows[0],
        id: Number(new Date()),
        title: newTitle || 'Untitled Policy',
        description: newDescription,
        subDescription: newSubDescription,
        images: draftImages,
        updatedAt: nowIso,
      };

      return [draft, ...rows];
    }


    return rows;
  }, [
    editingRow,
    rows,
    editTitle,
    editDescription,
    editSubDescription,
    editImagePreviewUrl,
    editDocPreviewUrl,
    newModalOpen,
    showPRCPreview,
    newTitle,
    newDescription,
    newSubDescription,
    newImagePreviewUrl,
    newDocPreviewUrl,
  ]);

  return (
    <div>
      <Snackbar
        open={showSaved}
        autoHideDuration={4000}
        onClose={() => setShowSaved(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowSaved(false)}
          icon={<ThumbUp fontSize="inherit" />}
          severity="success"
          sx={{ width: '100%' }}
        >
          Policy saved successfully
        </Alert>
      </Snackbar>

      <Card sx={{ padding: '2em' }}>
        <DataGridPro
          loading={loading}
          style={{ height: 520, width: '100%' }}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id || ''}
          pagination
          paginationMode="server"
          filterMode="server"
          sortingMode="server"
          rowCount={rowCount}
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onSortModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setSortModel(model);
          }}
          onPageChange={(newPage: number) => {
            setPaginationModel((prev) => ({ ...prev, page: newPage }));
          }}
          onPageSizeChange={(newPageSize: number) => {
            setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }));
          }}
          onFilterModelChange={(model) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            const normalized = normalizeFilterModel(model);
            if (!sameModel(normalized, filterModelApplied)) {
              setFilterModelApplied(normalized);
            }
          }}
          rowsPerPageOptions={[25, 50, 100]}
          rowHeight={64}
          headerHeight={56}
          sx={{
            '& .MuiDataGrid-cell': {
              boxSizing: 'border-box',
            },
            '& .MuiDataGrid-cell--pinnedRight': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell.actionsCell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingTop: 0,
              paddingBottom: 0,
              gap: 0.5,
            },
            '& .MuiDataGrid-cell.actionsCell .MuiIconButton-root': {
              padding: 1,
            },
          }}
          components={{
            Toolbar: CustomToolbar,
            Footer: GridPaginationBar,
            LoadingOverlay: () => (
              <GridOverlay>
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <CircularProgress />
                </div>
              </GridOverlay>
            ),
          }}
          componentsProps={{
            toolbar: {
              openFilterModal: () => {},
              filterModelDraft,
              setFilterModelDraft,
              handleFetchVerificationData: () => {},
            },
          }}
          initialState={{
            pinnedColumns: { right: ['actions'] },
          }}
        />

       {/* Add Policy Modal */}
        <StandardDialog
        title="Add Policy"
        subtitle="Create a new policy entry to be displayed in the Partner Resource Center."
        handleClose={handleCloseAdd}
        isOpen={newModalOpen}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
            >
                {newImagePreviewUrl ? (
                <img
                    src={newImagePreviewUrl}
                    alt={newTitle || 'Policy image'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                ) : (
                <Typography variant="caption" color="text.secondary">
                    No Image
                </Typography>
                )}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {newDocFile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                    Selected Document
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2">{newDocFile.name}</Typography>

                    {newDocPreviewUrl && (
                        <a
                        href={newDocPreviewUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                        >
                        <Typography variant="body2" color="primary" component="span">
                            Open
                        </Typography>
                        </a>
                    )}
                    </Box>
                </Box>
                ) : (
                <Typography variant="caption" color="text.secondary">
                    No document selected yet.
                </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" component="label" disabled={isAdding}>
                    Upload Image *
                    <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewImageFile(e.target.files?.[0] ?? null)}
                    />
                </Button>

                <Button variant="outlined" component="label" disabled={isAdding}>
                    Upload Document *
                    <input
                    hidden
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => setNewDocFile(e.target.files?.[0] ?? null)}
                    />
                </Button>
                </Box>

                {(newImageFile || newDocFile) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {newImageFile && (
                    <Typography variant="caption" color="text.secondary">
                        New image: {newImageFile.name}
                    </Typography>
                    )}
                    {newDocFile && (
                    <Typography variant="caption" color="text.secondary">
                        New document: {newDocFile.name}
                    </Typography>
                    )}
                </Box>
                )}
            </Box>
            </Box>

            <TextField
            label="Policy Title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            fullWidth
            disabled={isAdding}
            />

            <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            disabled={isAdding}
            />

            <TextField
            label="Sub Description"
            value={newSubDescription}
            onChange={(e) => setNewSubDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={isAdding}
            />

            {addError && (
            <Alert severity="error" onClose={() => setAddError('')}>
                {addError}
            </Alert>
            )}
        </Box>

        <StandardDialogActions>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
            <Button onClick={handleCloseAdd} disabled={isAdding}>
                Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
                variant="outlined"
                onClick={() => setShowPRCPreview(true)}
                disabled={isAdding}
                sx={{ mr: 1 }}
            >
                Live Preview
            </Button>
            <AsyncSaveButton
                variant="contained"
                onClick={handleAddNewPolicy}
                isSaving={isAdding}
                label="Save"
            />
            </Box>
        </StandardDialogActions>
        </StandardDialog>


        {/* Edit Policy Modal */}
        <StandardDialog
        isOpen={isEditOpen}
        title="Edit Policy"
        subtitle="Update the policy details, image, and the document that opens when users click the policy title."
        handleClose={handleCloseEdit}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
            >
                {(() => {
                  const previewUrl = editImagePreviewUrl ?? getResourceImageUrl(editingRow);

                  return previewUrl ? (
                    <img
                    src={previewUrl}
                    alt={editingRow?.title || 'Policy image'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                    No Image
                    </Typography>
                  );
                })()}
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
               {getResourceDocUrl(editingRow) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                    Current Document
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                        {getResourceDocName(editingRow)}
                    </Typography>
                    <a
                        href={getResourceDocUrl(editingRow)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <Typography variant="body2" color="primary" component="span">
                        Open
                        </Typography>
                    </a>
                    </Box>
                </Box>
               ) : (
                <Typography variant="caption" color="text.secondary">
                    No document uploaded yet.
                </Typography>
               )}

                {/* Replace actions */}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" component="label" disabled={isSaving}>
                    Replace Image
                    <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      setEditImageFile(file);
                    }}
                    />
                </Button>

                <Button variant="outlined" component="label" disabled={isSaving}>
                    Replace Document
                    <input
                    hidden
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      setEditDocFile(file);
                    }}
                    />
                </Button>
                </Box>

                {(editImageFile || editDocFile) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {editImageFile && (
                    <Typography variant="caption" color="text.secondary">
                        New image: {editImageFile.name}
                    </Typography>
                    )}
                    {editDocFile && (
                    <Typography variant="caption" color="text.secondary">
                        New document: {editDocFile.name}
                    </Typography>
                    )}
                </Box>
                )}
            </Box>
            </Box>

            <TextField
            label="Policy Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            fullWidth
            disabled={isSaving}
            />

            <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            disabled={isSaving}
            />

            <TextField
            label="Sub Description"
            value={editSubDescription}
            onChange={(e) => setEditSubDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            disabled={isSaving}
            />

            {editError && (
            <Alert severity="error" onClose={() => setEditError('')}>
                {editError}
            </Alert>
            )}
        </Box>

        <StandardDialogActions>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                <Button onClick={handleCloseEdit} disabled={isSaving}>Cancel</Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    onClick={() => setShowPRCPreview(true)}
                    disabled={isSaving}
                    sx={{ mr: 1 }}
                >
                    Live Preview
                </Button>
                <AsyncSaveButton variant="contained" onClick={handleSaveEdit} isSaving={isSaving} label="Save" />
            </Box>
        </StandardDialogActions>
        </StandardDialog>


        {/* Delete Policy Modal */}
        <StandardDialog isOpen={isDeleteOpen} title="Delete Policy?" handleClose={handleCloseDelete}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              Are you sure you want to delete <strong>{deletingRow?.title || 'this policy'}</strong>?
              This action cannot be undone.
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={handleCloseDelete} disabled={isDeleting}>
                Cancel
              </Button>
              <AsyncSaveButton onClick={handleDeleteConfirmed} isSaving={isDeleting} label="Delete" />
            </Box>
          </Box>
        </StandardDialog>
      </Card>
    <PartnerResourcesModal
        partnerResources={previewRows || []}
        showResourceModal={showPRCPreview}
        preventUrlChange
        previewCategory={RESOURCE_CATEGORY_TITLES.POLICIES}
        handleCloseResourceModal={() => setShowPRCPreview(false)}
        fetchResources={() => {
          if (policiesCategoryId) {
            fetchPolicies(policiesCategoryId);
          }
        }}
        />
    </div>
  );
};

export default PoliciesAdmin;
